import React, { useState, useMemo } from "react";
import { Share2 } from "lucide-react";
import { confrontoCompleto } from "../../utils/confrontoDipendente";

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCurrencyFull(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value);
}

export default function ConfrontoDipendenteSimulator({
  incassiAnno,
  profitabilityCoefficient,
  aliquota,
  inpsRate,
  inpsType,
  selectedYear,
  darkMode,
}) {
  const [inputValue, setInputValue] = useState(String(incassiAnno || ""));
  const [copied, setCopied] = useState(false);

  const fatturato = (() => {
    const cleaned = inputValue.replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
    return parseFloat(cleaned) || 0;
  })();

  const result = useMemo(() => {
    if (fatturato <= 0) return null;
    return confrontoCompleto(
      fatturato,
      { profitabilityCoefficient, taxRate: aliquota, inpsType: inpsType },
      selectedYear
    );
  }, [fatturato, profitabilityCoefficient, aliquota, inpsType, selectedYear]);

  const handleShare = async () => {
    if (!result) return;

    const text = [
      `Confronto Forfettario vs Dipendente (${selectedYear})`,
      ``,
      `Forfettario:`,
      `  Fatturato: ${formatCurrencyFull(result.forfettario.fatturato)}`,
      `  Tasse + contributi: ${formatCurrencyFull(result.forfettario.inps + result.forfettario.imposta)}`,
      `  Netto: ${formatCurrencyFull(result.forfettario.netto)}`,
      `  Aliquota effettiva: ${result.forfettario.aliquotaEffettiva.toFixed(1)}%`,
      ``,
      `Dipendente equivalente:`,
      `  RAL necessaria: ${formatCurrencyFull(result.dipendente.ral)}`,
      `  Costo azienda: ${formatCurrencyFull(result.dipendente.costoAzienda)}`,
      `  Netto: ${formatCurrencyFull(result.dipendente.netto)}`,
      ``,
      `Generato con Forfy`,
    ].join("\n");

    if (navigator.share) {
      try {
        await navigator.share({ title: "Confronto Forfettario vs Dipendente", text });
        return;
      } catch { /* user cancelled or not supported */ }
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard failed */ }
  };

  return (
    <div className="space-y-4">
      {/* Input fatturato */}
      <div>
        <label className={`text-xs font-medium ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
          Fatturato annuo lordo
        </label>
        <div className="relative mt-1">
          <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>€</span>
          <input
            type="text"
            inputMode="decimal"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value.replace(/[^0-9,.]/g, ""))}
            placeholder="Es. 40000"
            className={`w-full max-w-xs pl-8 pr-4 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 ${
              darkMode
                ? 'border-neutral-700 bg-neutral-800 text-white'
                : 'border-neutral-300 bg-white text-neutral-900'
            }`}
          />
        </div>
      </div>

      {result && (
        <>
          {/* Colonne confronto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Colonna Forfettario */}
            <div className={`rounded-xl p-4 ${darkMode ? 'bg-emerald-900/20 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
              <h4 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                Forfettario
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>Fatturato</span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                    {formatCurrency(result.forfettario.fatturato)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>INPS</span>
                  <span className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    -{formatCurrency(result.forfettario.inps)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>Imposta {aliquota}%</span>
                  <span className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    -{formatCurrency(result.forfettario.imposta)}
                  </span>
                </div>
                <div className={`border-t pt-2 mt-2 ${darkMode ? 'border-emerald-800' : 'border-emerald-200'}`}>
                  <div className="flex justify-between">
                    <span className={`text-sm font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Netto</span>
                    <span className={`text-sm font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                      {formatCurrency(result.forfettario.netto)}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Aliquota effettiva</span>
                    <span className={`text-xs ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                      {result.forfettario.aliquotaEffettiva.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Colonna Dipendente */}
            <div className={`rounded-xl p-4 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
              <h4 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                Dipendente equivalente
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>RAL necessaria</span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                    {formatCurrency(result.dipendente.ral)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>Costo azienda</span>
                  <span className={`text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                    {formatCurrency(result.dipendente.costoAzienda)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>INPS (9,19%)</span>
                  <span className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    -{formatCurrency(result.dipendente.inps)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>IRPEF</span>
                  <span className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    -{formatCurrency(result.dipendente.irpef)}
                  </span>
                </div>
                <div className={`border-t pt-2 mt-2 ${darkMode ? 'border-blue-800' : 'border-blue-200'}`}>
                  <div className="flex justify-between">
                    <span className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Netto</span>
                    <span className={`text-sm font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      {formatCurrency(result.dipendente.netto)}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Aliquota effettiva</span>
                    <span className={`text-xs ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                      {result.dipendente.aliquotaEffettiva.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* In pratica */}
          <div className={`rounded-xl p-4 ${darkMode ? 'bg-neutral-800' : 'bg-neutral-50'}`}>
            <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
              In pratica
            </h4>
            <p className={`text-sm ${darkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
              Con un fatturato di <strong>{formatCurrency(fatturato)}</strong> come forfettario,
              il tuo netto stimato è <strong>{formatCurrency(result.forfettario.netto)}</strong>.
              Per ottenere lo stesso netto come dipendente, dovresti avere una RAL di circa{" "}
              <strong>{formatCurrency(result.dipendente.ral)}</strong>,
              con un costo per l'azienda di <strong>{formatCurrency(result.dipendente.costoAzienda)}</strong>.
            </p>
          </div>

          {/* Bottone condividi */}
          <button
            type="button"
            onClick={handleShare}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
              darkMode
                ? 'text-neutral-400 hover:bg-neutral-800 border border-neutral-700'
                : 'text-neutral-600 hover:bg-neutral-100 border border-neutral-200'
            }`}
          >
            <Share2 className="w-4 h-4" />
            {copied ? "Copiato!" : "Condividi"}
          </button>
        </>
      )}

      {!result && fatturato <= 0 && (
        <p className={`text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
          Inserisci un fatturato per vedere il confronto.
        </p>
      )}
    </div>
  );
}
